import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>Contact Us | Uniform Supplier & Manufacturer</title>
  <meta name="description" content="Get in touch with us for bulk uniform orders, custom designs, and business inquiries. Reliable uniform supplier in India." />
  <meta name="keywords" content="contact uniform supplier, bulk uniform order India, uniform manufacturer contact" />
  <meta property="og:title" content="Premium Uniform Manufacturer in India" />
  <meta property="og:description" content="High-quality uniforms for hotels, hospitals, and schools." />
  <meta property="og:type" content="website" />
</Helmet>



const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Social icons
const TwitterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const Contact = () => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create WhatsApp message with form data
    const message = encodeURIComponent(
      `*New Inquiry from Website*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Company:* ${formData.company}\n` +
      `*Category:* ${formData.category}\n` +
      `*Message:* ${formData.message}`
    );

    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/916398130510?text=${message}`, '_blank');

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        category: '',
        message: ''
      });
    }, 1000);
  };

  return (
    <div className={`pt-24 md:pt-28 ${isDarkMode ? 'bg-zinc-900' : ''}`} data-testid="contact-page">
      {/* Hero Section */}
      <section className={`py-16 md:py-24 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#f9fafb]'}`}>
        <div className="container-custom">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Get in Touch</p>
            <h1 
              className={`text-3xl sm:text-4xl lg:text-5xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
              style={{ fontFamily: 'Bodoni Moda, serif' }}
            >
              Contact Us
            </h1>
            <div className="gold-line mx-auto mb-8" />
            <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Ready to elevate your team's appearance? We're here to help you find 
              the perfect uniform solution.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-900' : ''}`}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <h2 
                className={`text-2xl md:text-3xl mb-8 ${isDarkMode ? 'text-white' : ''}`}
                style={{ fontFamily: 'Bodoni Moda, serif' }}
              >
                Let's Start a Conversation
              </h2>
              <p className={`mb-10 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Whether you're looking for uniforms for a boutique hotel or a large 
                hospital chain, we're ready to understand your needs and deliver 
                excellence. Reach out through any of the channels below.
              </p>

              {/* Contact Cards */}
              <div className="space-y-6 mb-12">
                <div className={`flex items-start gap-4 p-6 transition-colors ${
                  isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-[#f9fafb] hover:bg-gray-100'
                }`}>
                  <div className="text-[#D4AF37]">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Call Us</h3>
                    <a href="tel:+916398130510" className={`block hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      +91 6398130510
                    </a>
                    <a href="tel:+917037738809" className={`block hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      +91 7037738809
                    </a>
                  </div>
                </div>

                <div className={`flex items-start gap-4 p-6 transition-colors ${
                  isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-[#f9fafb] hover:bg-gray-100'
                }`}>
                  <div className="text-[#D4AF37]">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Email Us</h3>
                    <a href="mailto:theisticmalabis@gmail.com" className={`hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      theisticmalabis@gmail.com
                    </a>
                  </div>
                </div>

                <div className={`flex items-start gap-4 p-6 transition-colors ${
                  isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-[#f9fafb] hover:bg-gray-100'
                }`}>
                  <div className="text-[#D4AF37]">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Visit Us</h3>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Dehradun, Uttarakhand<br />
                      India
                    </p>
                  </div>
                </div>

                <div className={`flex items-start gap-4 p-6 transition-colors ${
                  isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-[#f9fafb] hover:bg-gray-100'
                }`}>
                  <div className="text-[#D4AF37]">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : ''}`}>Business Hours</h3>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                      Monday - Saturday<br />
                      10:00 AM - 7:00 PM IST
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className={`text-xs uppercase tracking-widest font-medium mb-4 ${isDarkMode ? 'text-gray-300' : ''}`}>Connect With Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://wa.me/916398130510"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 border flex items-center justify-center transition-all ${
                      isDarkMode 
                        ? 'border-zinc-700 text-gray-400 hover:text-[#25D366] hover:border-[#25D366]' 
                        : 'border-gray-200 text-gray-600 hover:text-[#25D366] hover:border-[#25D366]'
                    }`}
                    data-testid="contact-whatsapp"
                    aria-label="WhatsApp"
                  >
                    <WhatsAppIcon />
                  </a>
                  <a
                    href="https://x.com/theisticmalabis?s=11"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 border flex items-center justify-center transition-all ${
                      isDarkMode 
                        ? 'border-zinc-700 text-gray-400 hover:text-white hover:border-white' 
                        : 'border-gray-200 text-gray-600 hover:text-black hover:border-black'
                    }`}
                    data-testid="contact-twitter"
                    aria-label="Twitter"
                  >
                    <TwitterIcon />
                  </a>
                  <a
                    href="https://instagram.com/malabisthestic"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 border flex items-center justify-center transition-all ${
                      isDarkMode 
                        ? 'border-zinc-700 text-gray-400 hover:text-[#E4405F] hover:border-[#E4405F]' 
                        : 'border-gray-200 text-gray-600 hover:text-[#E4405F] hover:border-[#E4405F]'
                    }`}
                    data-testid="contact-instagram"
                    aria-label="Instagram"
                  >
                    <InstagramIcon />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className={`p-8 md:p-12 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#f9fafb]'}`}>
                <h3 
                  className={`text-xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  Send Us an Inquiry
                </h3>

                {isSubmitted ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <MessageCircle className="text-green-600" size={32} />
                    </div>
                    <h4 className={`text-xl mb-3 ${isDarkMode ? 'text-white' : ''}`} style={{ fontFamily: 'Bodoni Moda, serif' }}>
                      Thank You!
                    </h4>
                    <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Your inquiry has been sent via WhatsApp. We'll respond shortly.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`}
                      data-testid="send-another"
                    >
                      Send Another Inquiry
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                          Full Name *
                        </Label>
                        <Input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className={`form-input rounded-none ${
                            isDarkMode 
                              ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                              : 'border-gray-200 focus:border-[#D4AF37]'
                          }`}
                          placeholder="Your name"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                          Email Address *
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className={`form-input rounded-none ${
                            isDarkMode 
                              ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                              : 'border-gray-200 focus:border-[#D4AF37]'
                          }`}
                          placeholder="your@email.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                          Phone Number *
                        </Label>
                        <Input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className={`form-input rounded-none ${
                            isDarkMode 
                              ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                              : 'border-gray-200 focus:border-[#D4AF37]'
                          }`}
                          placeholder="+91 XXXXX XXXXX"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                          Company / Institution
                        </Label>
                        <Input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className={`form-input rounded-none ${
                            isDarkMode 
                              ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                              : 'border-gray-200 focus:border-[#D4AF37]'
                          }`}
                          placeholder="Hotel/Hospital name"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="category" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                        Interested In
                      </Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`form-input w-full border rounded-none ${
                          isDarkMode 
                            ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                            : 'border-gray-200 bg-white focus:border-[#D4AF37]'
                        }`}
                        data-testid="input-category"
                      >
                        <option value="">Select a category</option>
                        <option value="hotel-reception">Hotel - Reception</option>
                        <option value="hotel-housekeeping">Hotel - Housekeeping</option>
                        <option value="hotel-chef">Hotel - Chef & Kitchen</option>
                        <option value="hotel-security">Hotel - Security</option>
                        <option value="hotel-fnb">Hotel - F&B Service</option>
                        <option value="hospital-nurses">Hospital - Nurses</option>
                        <option value="hospital-lab">Hospital - Lab Technicians</option>
                        <option value="custom">Custom Requirements</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="message" className={`text-xs uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                        Your Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className={`form-input form-textarea rounded-none resize-none ${
                          isDarkMode 
                            ? 'bg-zinc-900 border-zinc-700 text-white focus:border-[#D4AF37]' 
                            : 'border-gray-200 focus:border-[#D4AF37]'
                        }`}
                        placeholder="Tell us about your requirements..."
                        data-testid="input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-3 disabled:opacity-50"
                      data-testid="submit-form"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send size={16} />
                          Send Inquiry via WhatsApp
                        </>
                      )}
                    </button>

                    <p className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      By submitting, you'll be redirected to WhatsApp to complete your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact Banner */}
      <section className={`py-16 md:py-24 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#0a0a0a]'} text-white`}>
        <div className="container-custom text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 
              className="text-2xl md:text-3xl mb-6"
              style={{ fontFamily: 'Bodoni Moda, serif' }}
            >
              Prefer to Chat Directly?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              For quick inquiries and instant responses, reach out to us on WhatsApp. 
              We're available during business hours.
            </p>
            <a
              href="https://wa.me/916398130510?text=Hello%20MALABISthestic!%20I%20would%20like%20to%20inquire%20about%20uniforms."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
              data-testid="direct-whatsapp"
              style={{ backgroundColor: '#25D366' }}
            >
              <WhatsAppIcon />
              Chat on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
